import { supabase } from '../lib/supabase';
import analyticsService from './analyticsService';
import conversionTrackingService from './conversionTrackingService';

/**
 * Subscription Service
 * Manages company subscriptions, plan upgrades, and usage metrics
 */

// Plan configurations
const PLAN_CONFIGS = {
  free: {
    name: 'Free',
    price: 0,
    currency: 'CAD',
    teamMemberLimit: 3,
    candidateUnlocksPerMonth: -1, // Unlimited
    isHidden: true, // Hidden from public, admin-assignable only
    features: [
      'All Standard features',
      'Unlimited candidate unlocks',
      'Full candidate details in emails',
      'Up to 3 team members',
      '1 active company',
      'Admin-assigned promotional plan'
    ]
  },
  freemium: {
    name: 'Freemium',
    price: 0,
    currency: 'CAD',
    teamMemberLimit: 1,
    candidateUnlocksPerMonth: 1,
    features: [
      'Unlimited job postings',
      'Unlimited locations',
      '1 candidate unlock/month',
      '1 team member',
      'Basic email notifications'
    ]
  },
  standard: {
    name: 'Standard',
    price: 100,
    currency: 'CAD',
    teamMemberLimit: 3,
    candidateUnlocksPerMonth: -1, // Unlimited
    features: [
      'All Freemium features',
      'Unlimited candidate unlocks',
      'Full candidate details in emails',
      'Up to 3 team members',
      '1 active company'
    ]
  },
  premium: {
    name: 'Premium',
    price: 300,
    currency: 'CAD',
    teamMemberLimit: 5,
    candidateUnlocksPerMonth: -1, // Unlimited
    features: [
      'All Standard features',
      'Candidate browsing (Coming Soon)',
      'Credit-based reach-out system',
      'Up to 5 team members',
      'Priority support'
    ]
  }
};

/**
 * Get current subscription for a company
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Subscription data
 */
export const getCurrentSubscription = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('company_subscriptions')?.select('*')?.eq('company_id', companyId)?.single();

    if (error) throw error;

    return {
      success: true,
      data: {
        ...data,
        planConfig: PLAN_CONFIGS?.[data?.plan] || PLAN_CONFIGS?.freemium
      }
    };
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

/**
 * Get usage metrics for a company
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Usage metrics
 */
export const getUsageMetrics = async (companyId) => {
  try {
    // Get subscription
    const subResult = await getCurrentSubscription(companyId);
    if (!subResult?.success) throw new Error('Failed to fetch subscription');

    const subscription = subResult?.data;
    const plan = subscription?.plan || 'freemium';
    const planConfig = PLAN_CONFIGS?.[plan];

    // Get current month
    const currentMonth = new Date()?.toISOString()?.slice(0, 7); // YYYY-MM

    // Get monthly quota
    const { data: quotaData, error: quotaError } = await supabase?.from('monthly_unlock_quotas')?.select('*')?.eq('company_id', companyId)?.eq('month_year', currentMonth)?.single();

    if (quotaError && quotaError?.code !== 'PGRST116') throw quotaError;

    // Get team member count
    const { count: teamMemberCount, error: teamError } = await supabase?.from('company_team_members')?.select('*', { count: 'exact', head: true })?.eq('company_id', companyId)?.eq('status', 'active');

    if (teamError) throw teamError;

    // Get total unlocks this month
    const { count: unlocksCount, error: unlocksError } = await supabase?.from('candidate_unlocks')?.select('*', { count: 'exact', head: true })?.eq('company_id', companyId)?.gte('unlocked_at', `${currentMonth}-01`);

    if (unlocksError) throw unlocksError;

    return {
      success: true,
      data: {
        plan,
        planConfig,
        candidateUnlocks: {
          used: quotaData?.unlocks_used || unlocksCount || 0,
          limit: plan === 'freemium' ? (quotaData?.unlocks_limit || 1) : -1, // -1 = unlimited
          remaining: plan === 'freemium' ? Math.max(0, (quotaData?.unlocks_limit || 1) - (quotaData?.unlocks_used || 0)) : -1,
          resetAt: quotaData?.reset_at
        },
        teamMembers: {
          current: (teamMemberCount || 0) + 1, // +1 for owner
          limit: planConfig?.teamMemberLimit || 1
        }
      }
    };
  } catch (error) {
    console.error('Error fetching usage metrics:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

/**
 * Check if company can add team member
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Can add result
 */
export const canAddTeamMember = async (companyId) => {
  try {
    const metricsResult = await getUsageMetrics(companyId);
    if (!metricsResult?.success) throw new Error('Failed to fetch metrics');

    const { teamMembers } = metricsResult?.data;
    const canAdd = teamMembers?.current < teamMembers?.limit;

    return {
      success: true,
      canAdd,
      reason: canAdd ? null : `Team member limit reached (${teamMembers?.current}/${teamMembers?.limit}). Upgrade your plan to add more members.`,
      current: teamMembers?.current,
      limit: teamMembers?.limit
    };
  } catch (error) {
    console.error('Error checking team member limit:', error);
    return {
      success: false,
      canAdd: false,
      reason: 'Failed to check team member limit',
      error: error?.message
    };
  }
};

/**
 * Get payment history for a company
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Payment history
 */
export const getPaymentHistory = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('payment_history')?.select('*')?.eq('company_id', companyId)?.order('payment_date', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Error fetching payment history:', error);
    return {
      success: false,
      error: error?.message,
      data: []
    };
  }
};

/**
 * Update subscription plan (called by webhook)
 * @param {string} companyId - Company UUID
 * @param {Object} updateData - Subscription update data
 * @returns {Promise<Object>} - Update result
 */
export const updateSubscription = async (companyId, updateData) => {
  try {
    const { data, error } = await supabase?.from('company_subscriptions')?.update({
        ...updateData,
        updated_at: new Date()?.toISOString()
      })?.eq('company_id', companyId)?.select()?.single();

    if (error) throw error;

    // Track subscription conversion
    if (updateData?.plan && updateData?.plan !== 'freemium') {
      const planConfig = PLAN_CONFIGS?.[updateData?.plan];
      analyticsService?.trackSubscription(
        planConfig?.name,
        planConfig?.price,
        'monthly',
        companyId
      );
      
      conversionTrackingService?.trackSubscriptionFunnel(
        'subscription_completed',
        companyId,
        planConfig?.name,
        { price: planConfig?.price }
      );
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error updating subscription:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Get plan configurations
 * @returns {Object} - Plan configurations
 */
export const getPlanConfigs = () => PLAN_CONFIGS;

export default {
  getCurrentSubscription,
  getUsageMetrics,
  canAddTeamMember,
  getPaymentHistory,
  updateSubscription,
  getPlanConfigs
};
function subscriptionService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: subscriptionService is not implemented yet.', args);
  return null;
}

export { subscriptionService };